import { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DayPicker } from 'react-day-picker';
import { toast } from 'react-toastify';
import 'react-day-picker/style.css';
import { Modal } from '../../common/Modal/Modal';
import { bookingApi } from '../../../services/bookingService';
import { getErrorMessage } from '../../../utils/errors';
import { formatDateToIso } from '../../../utils/availability';
import { BOOKING_TIMEZONE } from '../../../types';
import './AvailabilityModal.css';

interface AvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TIME_OPTIONS = [
  { value: '09:00', label: '9:00 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '11:00', label: '11:00 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '13:00', label: '1:00 PM' },
  { value: '14:00', label: '2:00 PM' },
  { value: '15:00', label: '3:00 PM' },
  { value: '16:00', label: '4:00 PM' },
  { value: '17:00', label: '5:00 PM' },
  { value: '18:00', label: '6:00 PM' },
  { value: '19:00', label: '7:00 PM' },
  { value: '20:00', label: '8:00 PM' },
];

export function AvailabilityModal({ isOpen, onClose }: AvailabilityModalProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.resolvedLanguage?.startsWith('ar') ?? false;

  const [availabilityMap, setAvailabilityMap] = useState<Record<string, string[]>>({});
  const [selectedDays, setSelectedDays] = useState<Date[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copiedTimes, setCopiedTimes] = useState<string[] | null>(null);

  // Weekly repeat state
  const [repeatWeekdays, setRepeatWeekdays] = useState<number[]>([]);
  const [repeatWeeks, setRepeatWeeks] = useState(4);

  const selectedIsos = useMemo(
    () => selectedDays.map((d) => formatDateToIso(d)).sort(),
    [selectedDays]
  );

  // Load existing availability when modal opens
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setLoading(true);

    bookingApi.getAvailableDates()
      .then((configs) => {
        if (cancelled) return;
        const initialMap: Record<string, string[]> = {};
        configs.forEach((c) => {
          initialMap[c.date] = c.times;
        });
        setAvailabilityMap(initialMap);
      })
      .catch(() => {
        if (!cancelled) {
          toast.error(t('admin.availabilityLoadFailed', 'Failed to load availability'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, t]);

  const activeTimes = useMemo(() => {
    // Hours pane reflects the first selected day that already has hours configured
    const firstConfigured = selectedIsos.find((iso) => (availabilityMap[iso] || []).length > 0);
    return firstConfigured ? availabilityMap[firstConfigured] : [];
  }, [availabilityMap, selectedIsos]);

  const handleDaysSelect = useCallback((days: Date[] | undefined) => {
    setSelectedDays(days ?? []);
  }, []);

  const toggleTime = useCallback((time: string) => {
    if (selectedIsos.length === 0) return;
    setAvailabilityMap((current) => {
      const everywhereSelected = selectedIsos.every((iso) =>
        (current[iso] || []).includes(time)
      );
      const next = { ...current };
      selectedIsos.forEach((iso) => {
        const times = next[iso] || [];
        next[iso] = (
          everywhereSelected ? times.filter((v) => v !== time) : [...times, time]
        ).sort();
      });
      return next;
    });
  }, [selectedIsos]);

  const applyPreset = useCallback((preset: 'workday' | 'evening' | 'clear' | 'all') => {
    if (selectedIsos.length === 0) return;
    setAvailabilityMap((current) => {
      let times: string[] = [];
      if (preset === 'workday') {
        times = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
      } else if (preset === 'evening') {
        times = ['17:00', '18:00', '19:00', '20:00'];
      } else if (preset === 'all') {
        times = TIME_OPTIONS.map((o) => o.value);
      } // 'clear' leaves times as empty array

      const next = { ...current };
      selectedIsos.forEach((iso) => {
        next[iso] = times;
      });
      return next;
    });
  }, [selectedIsos]);

  const handleCopy = useCallback(() => {
    setCopiedTimes(activeTimes);
    toast.info(t('admin.copiedTimes', 'Copied times for {{date}}', { date: selectedIsos[0] || '' }));
  }, [activeTimes, selectedIsos, t]);

  const handlePaste = useCallback(() => {
    if (!copiedTimes || selectedIsos.length === 0) return;
    setAvailabilityMap((current) => {
      const next = { ...current };
      selectedIsos.forEach((iso) => {
        next[iso] = [...copiedTimes];
      });
      return next;
    });
    toast.success(
      t('admin.pastedTimesCount', 'Pasted times to {{count}} day(s)', { count: selectedIsos.length })
    );
  }, [copiedTimes, selectedIsos, t]);

  const toggleRepeatWeekday = useCallback((weekday: number) => {
    setRepeatWeekdays((current) =>
      current.includes(weekday)
        ? current.filter((d) => d !== weekday)
        : [...current, weekday].sort()
    );
  }, []);

  const handleApplyRepeat = useCallback(() => {
    if (repeatWeekdays.length === 0 || repeatWeeks < 1) return;
    const times =
      activeTimes.length > 0
        ? activeTimes
        : ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];

    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() + 1); // start from tomorrow

    const isos: string[] = [];
    for (let i = 0; i < repeatWeeks * 7; i += 1) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      if (repeatWeekdays.includes(d.getDay())) {
        isos.push(formatDateToIso(d));
      }
    }

    setAvailabilityMap((current) => {
      const next = { ...current };
      isos.forEach((iso) => {
        next[iso] = Array.from(new Set([...(next[iso] || []), ...times])).sort();
      });
      return next;
    });
    toast.success(
      t('admin.repeatApplied', 'Added hours to {{count}} days', { count: isos.length })
    );
  }, [repeatWeekdays, repeatWeeks, activeTimes, t]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      // Build DTO format: Array of { date, times }
      const payload = Object.keys(availabilityMap)
        .filter((d) => availabilityMap[d] && availabilityMap[d].length > 0)
        .map((d) => ({
          date: d,
          times: availabilityMap[d],
        }));

      await bookingApi.setAvailableDates(payload);
      toast.success(t('admin.availabilitySaved', 'Availability updated successfully'));
      onClose();
    } catch (err) {
      const message = await getErrorMessage(err, t('admin.availabilitySaveFailed', 'Failed to save availability'));
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }, [availabilityMap, onClose, t]);

  const disablePastDates = useCallback((date: Date) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return date <= d;
  }, []);

  // Modifiers to highlight configured dates with a green indicator in the DayPicker
  const configuredDates = useMemo(() => {
    return Object.keys(availabilityMap)
      .filter((d) => availabilityMap[d] && availabilityMap[d].length > 0)
      .map((d) => new Date(`${d}T00:00:00`));
  }, [availabilityMap]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="availability-modal">
      <div className="avail-modal-header">
        <div className="avail-modal-icon" aria-hidden>
          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </div>
        <div>
          <h2 className="avail-modal-title">{t('admin.manageAvailability', 'Manage Availability')}</h2>
          <p className="avail-modal-subtitle">
            {t('admin.availabilityHint', 'Select a date on the calendar, then configure its available time slots')}
          </p>
        </div>
      </div>

      <div className="avail-modal-body">
        {loading ? (
          <div className="avail-modal-loading">
            <span className="auth-spinner auth-spinner-brand" />
          </div>
        ) : (
          <div className="avail-grid-layout">
            {/* Left Column: Calendar */}
            <div className="avail-calendar-pane">
              <h3 className="avail-pane-title">{t('admin.selectDate', '1. Select Date(s)')}</h3>
              <DayPicker
                mode="multiple"
                selected={selectedDays}
                onSelect={handleDaysSelect}
                disabled={disablePastDates}
                startMonth={new Date()}
                weekStartsOn={0}
                dir={isRtl ? 'rtl' : 'ltr'}
                className="avail-day-picker"
                modifiers={{
                  configured: configuredDates,
                }}
                modifiersClassNames={{
                  configured: 'rdp-day_configured',
                }}
              />

              {/* Weekly Repeat */}
              <div className="avail-repeat">
                <h4 className="avail-repeat-title">{t('admin.repeatWeekly', 'Repeat weekly')}</h4>
                <div className="avail-repeat-weekdays">
                  {[0, 1, 2, 3, 4, 5, 6].map((weekday) => {
                    const label = new Intl.DateTimeFormat(i18n.resolvedLanguage, {
                      weekday: 'short',
                    }).format(new Date(2024, 0, 7 + weekday)); // Jan 7 2024 = Sunday
                    const isChecked = repeatWeekdays.includes(weekday);
                    return (
                      <button
                        type="button"
                        key={weekday}
                        className={`avail-weekday-chip${isChecked ? ' is-active' : ''}`}
                        onClick={() => toggleRepeatWeekday(weekday)}
                        aria-pressed={isChecked}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div className="avail-repeat-row">
                  <label className="avail-repeat-label">
                    {t('admin.repeatWeeks', 'Weeks')}
                    <input
                      type="number"
                      className="avail-repeat-weeks-input"
                      min={1}
                      max={104}
                      value={repeatWeeks}
                      onChange={(e) =>
                        setRepeatWeeks(Math.max(1, Math.min(104, Number(e.target.value) || 1)))
                      }
                    />
                  </label>
                  <button
                    type="button"
                    className="avail-preset-btn"
                    onClick={handleApplyRepeat}
                    disabled={repeatWeekdays.length === 0}
                  >
                    {t('admin.applyRepeat', 'Apply')}
                  </button>
                </div>
                <p className="avail-repeat-hint">
                  {t(
                    'admin.repeatHint',
                    'Applies the hours on the right (or 9 AM - 5 PM if none) to the chosen weekdays for the next N weeks.'
                  )}
                </p>
              </div>
            </div>

            {/* Right Column: Time Slot Builder */}
            <div className="avail-time-pane">
              <div className="avail-time-pane-header">
                <h3 className="avail-pane-title">
                  {t('admin.configureTimes', '2. Available Times')}
                </h3>
                <span className="avail-active-date-badge">
                  {t('admin.daysSelected', '{{count}} day(s) selected', { count: selectedIsos.length })}
                </span>
              </div>

              {selectedIsos.length === 0 && (
                <p className="avail-no-selection-hint">
                  {t('admin.noDaysSelectedHint', 'Select one or more days on the calendar to configure their hours.')}
                </p>
              )}

              {/* Presets Toolbar */}
              <div className="avail-presets-row">
                <button
                  type="button"
                  className="avail-preset-btn"
                  onClick={() => applyPreset('workday')}
                  disabled={selectedIsos.length === 0}
                >
                  {t('admin.presetWorkday', '9 AM - 5 PM')}
                </button>
                <button
                  type="button"
                  className="avail-preset-btn"
                  onClick={() => applyPreset('evening')}
                  disabled={selectedIsos.length === 0}
                >
                  {t('admin.presetEvening', '5 PM - 8 PM')}
                </button>
                <button
                  type="button"
                  className="avail-preset-btn"
                  onClick={() => applyPreset('all')}
                  disabled={selectedIsos.length === 0}
                >
                  {t('admin.presetAll', 'All')}
                </button>
                <button
                  type="button"
                  className="avail-preset-btn text-danger"
                  onClick={() => applyPreset('clear')}
                  disabled={selectedIsos.length === 0}
                >
                  {t('admin.presetClear', 'Clear')}
                </button>
              </div>

              {/* Copy/Paste Clipboard */}
              <div className="avail-clipboard-row">
                <button
                  type="button"
                  className="avail-clipboard-btn"
                  onClick={handleCopy}
                  disabled={selectedIsos.length === 0}
                  title="Copy current active times configuration"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  {t('admin.copyTimes', 'Copy Hours')}
                </button>
                <button
                  type="button"
                  className="avail-clipboard-btn"
                  onClick={handlePaste}
                  disabled={!copiedTimes || selectedIsos.length === 0}
                  title="Paste copied times configuration to selected days"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                  </svg>
                  {t('admin.pasteTimes', 'Paste Hours')}
                </button>
              </div>

              {/* Time Slots Grid */}
              <div className="avail-slots-grid">
                {TIME_OPTIONS.map((option) => {
                  const isChecked = activeTimes.includes(option.value);
                  return (
                    <button
                      type="button"
                      key={option.value}
                      className={`avail-slot-chip${isChecked ? ' is-active' : ''}`}
                      onClick={() => toggleTime(option.value)}
                      disabled={selectedIsos.length === 0}
                    >
                      <span className="avail-slot-checkbox-indicator">
                        {isChecked && (
                          <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </span>
                      {option.label}
                    </button>
                  );
                })}
              </div>

              <div className="avail-time-pane-footer">
                <div className="avail-info-row">
                  <span className="avail-info-label">{t('admin.selectedForDay', 'Active slots (first selected day)')}</span>
                  <span className="avail-info-value">{activeTimes.length}</span>
                </div>
                <div className="avail-info-row">
                  <span className="avail-info-label">{t('booking.timezone')}</span>
                  <span className="avail-info-value">{BOOKING_TIMEZONE}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="avail-modal-actions">
        <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>
          {t('booking.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-filled"
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? (
            <span className="avail-btn-loading">
              <span className="auth-spinner auth-spinner-white" />
              {t('admin.saving', 'Saving...')}
            </span>
          ) : (
            t('admin.saveAvailability', 'Save Availability')
          )}
        </button>
      </div>
    </Modal>
  );
}
